<template>
  <BaseDialog
    :open="open"
    :max-width="'840px'"
    @close="onClose"
  >
    <template #title>
      {{ headerTitle }}
    </template>

    <form
      class="form"
      @submit.prevent="onSubmit"
    >
      <div
        class="field field-set"
        style="display: grid;  grid-template-columns: 1fr 1fr;  gap: 8px;"
      >
        <div>
          <label for="lb-title">Titel</label>
          <input
            id="lb-title"
            v-model.trim="model.title"
            class="input"
            type="text"
            required
            placeholder="z. B. 9. Klasse - 25/26"
          >
        </div>

        <div>
          <label for="lb-footnote">Fußnote</label>
          <input
            id="lb-footnote"
            v-model.trim="model.footnote"
            class="input"
            type="text"
            placeholder="Lernbericht 2025/26"
          >
        </div>
      </div>

      <div class="field">
        <label for="subj-filter">Fächer</label>
        <div class="subjects-grid">
          <div class="col">
            <div class="chip-box">
              <div class="chips">
                <button
                  v-for="s in filteredAvailableSubjects"
                  :key="s.id"
                  type="button"
                  class="chip"
                  :disabled="subjectsLoading"
                  :title="s.title"
                  @click="addSubject(s.id)"
                >
                  {{ s.title }}<span v-if="s.abbreviation"> ({{ s.abbreviation }})</span>
                </button>
                <p
                  v-if="subjectsLoading"
                  class="muted"
                >
                  Fächer werden geladen…
                </p>
                <p
                  v-else-if="filteredAvailableSubjects.length===0"
                  class="muted"
                >
                  Keine Treffer.
                </p>
              </div>
            </div>
          </div>

          <div class="col">
            <div class="chip-box">
              <div class="chips column">
                <div
                  v-for="(s, idx) in selectedSubjects"
                  :key="s.id"
                  class="row"
                >
                  <span class="subject-label">
                    {{ s.title }}<span v-if="s.abbreviation"> ({{ s.abbreviation }})</span>
                  </span>
                  <div class="actions mini">
                    <button
                      class="btn icon-only"
                      type="button"
                      :disabled="idx===0"
                      title="Nach oben"
                      aria-label="Nach oben"
                      @click="moveUp(idx)"
                    >
                      <i
                        class="pi pi-arrow-up"
                        aria-hidden="true"
                      />
                    </button>
                    <button
                      class="btn icon-only"
                      type="button"
                      :disabled="idx===selectedSubjectIds.length-1"
                      title="Nach unten"
                      aria-label="Nach unten"
                      @click="moveDown(idx)"
                    >
                      <i
                        class="pi pi-arrow-down"
                        aria-hidden="true"
                      />
                    </button>
                    <button
                      class="btn danger icon-only"
                      type="button"
                      title="Entfernen"
                      aria-label="Entfernen"
                      @click="removeSubject(s.id)"
                    >
                      <i
                        class="pi pi-times"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>
                <p
                  v-if="selectedSubjectIds.length===0"
                  class="muted"
                >
                  Noch kein Fach ausgewählt.
                </p>
              </div>
            </div>
            <p class="help">
              Reihenfolge hier entspricht der späteren Darstellung im Lernbrief.
            </p>
          </div>
        </div>
      </div>

      <p
        v-if="error"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ error }}
      </p>

      <div class="actions">
        <button
          type="button"
          class="btn"
          @click="onClose"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="saving"
        >
          {{ saving ? 'Speichern…' : 'Speichern' }}
        </button>
      </div>
    </form>
  </BaseDialog>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import {listFeedbackSubjects} from '@/api/feedbackSubjects';

const props = defineProps({
  open: {type: Boolean, default: false},
  mode: {type: String, default: 'new'}, // 'new' | 'edit'
  initial: {type: Object, default: null},
});

const emit = defineEmits(['close', 'saved']);

const saving = ref(false);
const error = ref(null);

const model = ref({
  id: null,
  title: '',
  footnote: '',
});

// Fächer-Handling
const subjectsLoading = ref(false);
const subjectFilter = ref('');
const subjectsCatalog = ref([]); // alle Fächer
const selectedSubjectIds = ref([]); // Reihenfolge maßgeblich

const availableSubjects = computed(() => {
  const set = new Set(selectedSubjectIds.value.map(n => Number(n)));
  return (subjectsCatalog.value || []).filter(s => !set.has(Number(s.id)));
});
const filteredAvailableSubjects = computed(() => {
  const q = (subjectFilter.value || '').trim().toLowerCase();
  if (!q) return availableSubjects.value;
  return availableSubjects.value.filter(s => {
    const title = String(s.title || '').toLowerCase();
    const abbr = String(s.abbreviation || '').toLowerCase();
    return title.includes(q) || (!!abbr && abbr.includes(q));
  });
});
const selectedSubjects = computed(() => {
  const byId = new Map((subjectsCatalog.value || []).map(s => [Number(s.id), s]));
  return selectedSubjectIds.value.map(id => byId.get(Number(id))).filter(Boolean);
});

function addSubject(id) {
  const nid = Number(id);
  if (!Number.isFinite(nid)) return;
  if (selectedSubjectIds.value.includes(nid)) return;
  selectedSubjectIds.value = [...selectedSubjectIds.value, nid];
}

function removeSubject(id) {
  const nid = Number(id);
  selectedSubjectIds.value = selectedSubjectIds.value.filter(v => Number(v) !== nid);
}

function moveUp(index) {
  const i = Number(index);
  if (i <= 0) return;
  const arr = [...selectedSubjectIds.value];
  [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
  selectedSubjectIds.value = arr;
}

function moveDown(index) {
  const i = Number(index);
  const arr = [...selectedSubjectIds.value];
  if (i < 0 || i >= arr.length - 1) return;
  [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]];
  selectedSubjectIds.value = arr;
}

const headerTitle = computed(() => {
  if (props.mode === 'edit') return 'Lernbrief bearbeiten';
  return 'Lernbrief hinzufügen';
});

async function loadSubjects() {
  subjectsLoading.value = true;
  try {
    subjectsCatalog.value = await listFeedbackSubjects();
  } catch {
    subjectsCatalog.value = [];
  } finally {
    subjectsLoading.value = false;
  }
}

watch(
    () => props.open,
    async (isOpen) => {
      if (!isOpen) return;
      error.value = null;
      subjectFilter.value = '';

      // Katalog laden
      await loadSubjects();

      if (props.mode === 'edit' && props.initial) {
        model.value = {
          id: props.initial.id ?? null,
          title: props.initial.title ?? '',
          footnote: props.initial.footnote ?? '',
        };
        // Vorauswahl: bevorzugt subject_ids, Fallback aus subjects (sort)
        const fromIds = Array.isArray(props.initial.subject_ids) ? props.initial.subject_ids : null;
        const fromSubjects = Array.isArray(props.initial.subjects) ? props.initial.subjects : null;
        if (fromIds) {
          selectedSubjectIds.value = fromIds.map(n => Number(n)).filter(n => Number.isFinite(n));
        } else if (fromSubjects) {
          const ids = [...fromSubjects]
              .sort((a, b) => (Number(a.sort ?? 0) - Number(b.sort ?? 0)))
              .map(s => Number(s.id))
              .filter(n => Number.isFinite(n));
          selectedSubjectIds.value = ids;
        } else {
          selectedSubjectIds.value = [];
        }
      } else {
        model.value = {
          id: null,
          title: '',
          footnote: '',
        };
        selectedSubjectIds.value = [];
      }
    },
    {immediate: true}
);

function onClose() {
  emit('close');
}

async function onSubmit() {
  error.value = null;
  const title = String(model.value.title || '').trim();
  const footnote = String(model.value.footnote || '').trim();

  if (!title) {
    error.value = 'Titel ist erforderlich.';
    return;
  }
  if (!footnote) {
    error.value = 'Fußnote ist erforderlich.';
    return;
  }

  const subject_ids = selectedSubjectIds.value.map(n => Number(n)).filter(n => Number.isFinite(n));

  saving.value = true;
  try {
    emit('saved', {
      mode: props.mode,
      id: model.value.id,
      payload: {title, footnote, subject_ids}
    });
  } finally {
    saving.value = false;
  }
}
</script>

<style scoped>
.form {
  display: block;
}

.field label {
  display: block;
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 2px;
}

.input {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 8px;
  background: var(--surface);
}

.subjects-grid {
  display: grid;
  grid-template-columns: .8fr 1.2fr;
  gap: 8px;
  margin-top: 6px;
}

.subjects-grid .col {
  padding: 0;
}

.chip-box {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  padding: 8px;
  min-height: 80px;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 260px;
  overflow: auto;
}

.chips.column {
  flex-direction: column;
  gap: 8px;
}

.subjects-grid .chips {
  display: grid;
  overflow-y: scroll;
}

.subjects-grid .chips.column {
  gap: 6px;
}

.subjects-grid .chips.column .row:hover , .subjects-grid .chip-box button.chip:hover {
  background: whitesmoke;
}

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 6px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
}

.subject-label {
  font-size: 13px;
}

.chip {
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 13px;
  line-height: 1.4;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.actions.mini {
  gap: 4px;
}

.btn {
  height: 36px;
  padding: 0 12px;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 6px;
  cursor: pointer;
}

.btn.icon-only {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-primary {
  border: 1px solid var(--primary);
  background: var(--primary);
  color: #fff;
}

.muted {
  color: var(--muted);
}

.help {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
  margin-top: 6px;
}

:deep(.dialog-footer) {
  display: none;
}

@media (max-width: 720px) {
  .subjects-grid {
    grid-template-columns: 1fr;
  }
}
</style>
